import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import EmptyState from "@/components/base/EmptyState";
import DetailIntro from "@/pages/detail/components/DetailIntro";
import CastGrid from "@/pages/detail/components/CastGrid";
import EpisodePanel from "@/pages/detail/components/EpisodePanel";
import { getMovieById, getCastOf, getRelated, TYPE_LABELS } from "@/lib/media";
import { useFavorites, useRecentlyViewed } from "@/hooks/useLocalLibrary";
import { useToast } from "@/hooks/useToast";
import { usePageMeta } from "@/hooks/usePageMeta";
import type { Movie } from "@/lib/types";

const INFO_ROWS = (movie: Movie) => [
  { label: "类型", value: TYPE_LABELS[movie.type] },
  { label: "地区", value: movie.region },
  { label: "年份", value: String(movie.year) },
  { label: "片长 / 集数", value: movie.duration },
  { label: "导演", value: movie.director },
  { label: "编剧", value: movie.writer },
  { label: "语言", value: movie.language },
  { label: "状态", value: movie.status },
  { label: "最近更新", value: movie.updatedAt },
];

export default function DetailPage() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const { isFavorite, toggle } = useFavorites();
  const { track } = useRecentlyViewed();
  const { push } = useToast();

  const related = movie ? getRelated(movie, 4) : [];

  usePageMeta({
    title: movie ? `${movie.title}剧情介绍与演职员名单 - 华丽的外出` : "作品不存在 - 华丽的外出",
    description: movie
      ? `《${movie.title}》${movie.year}年${movie.region}${TYPE_LABELS[movie.type]}，${movie.tagline} 主演：${getCastOf(movie)
          .map((member) => member.actor?.name ?? "")
          .slice(0, 3)
          .filter(Boolean)
          .join("、")}。查看完整剧情介绍、演员名单与剧集信息。`
      : "该影视作品不存在或已下架，请返回首页浏览其他内容。",
    keywords: movie
      ? `${movie.title},${movie.title}剧情介绍,${movie.title}演员表,${movie.title}${TYPE_LABELS[movie.type]},${movie.director}`
      : "华丽的外出,影视作品",
  });

  useEffect(() => {
    if (movie) track(movie.id);
  }, [movie, track]);

  if (!movie) {
    return (
      <SiteLayout>
        <div className="shell py-16">
          <EmptyState
            icon="ri-film-line"
            title="没有找到这部作品"
            desc="它可能已被移出片库，或者链接输入有误。你可以回到首页看看本周的焦点推荐。"
            actionTo="/"
            actionLabel="返回首页"
            action={
              <Link
                to="/category/all"
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                浏览全部作品
              </Link>
            }
          />
        </div>
      </SiteLayout>
    );
  }

  const favorite = isFavorite(movie.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      push("链接已复制，可以分享给同样在追这部剧的人", "accent");
    } catch {
      push("复制失败，请从浏览器地址栏手动复制链接");
    }
  };

  const handleFavorite = () => {
    toggle(movie.id);
    push(favorite ? `已将《${movie.title}》移出收藏` : `已收藏《${movie.title}》，可在详情页随时查看`);
  };

  return (
    <SiteLayout>
      <DetailIntro
        movie={movie}
        isFavorite={favorite}
        onToggleFavorite={handleFavorite}
        onShare={handleShare}
      />

      <div className="shell mt-8 md:mt-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            <section aria-label="剧情介绍">
              <div className="flex items-center gap-2">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                <h2 className="text-lg font-bold text-foreground-950 md:text-xl">剧情介绍</h2>
              </div>
              <div className="mt-3.5 space-y-3.5 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                {movie.synopsis.map((paragraph, index) => (
                  <p key={index} className="text-sm leading-[1.9] text-foreground-700">
                    {paragraph}
                  </p>
                ))}
                <div className="flex flex-wrap gap-1.5 border-t border-background-200 pt-4">
                  {movie.genres.map((genre) => (
                    <span
                      key={genre}
                      className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800"
                    >
                      {genre}
                    </span>
                  ))}
                </div>
              </div>
            </section>

            <CastGrid movie={movie} />
            <EpisodePanel movie={movie} />
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <section className="rounded-lg border border-background-200 bg-background-100/60 p-4 md:p-5" aria-label="作品资料">
                <h2 className="text-base font-bold text-foreground-950">作品资料</h2>
                <dl className="mt-3 divide-y divide-background-200/80">
                  {INFO_ROWS(movie).map((row) => (
                    <div key={row.label} className="flex items-start justify-between gap-3 py-2">
                      <dt className="shrink-0 text-xs text-foreground-500">{row.label}</dt>
                      <dd className="text-right text-xs font-medium text-foreground-800">{row.value}</dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-3 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5">
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-accent-900">
                    <i className="ri-bookmark-3-line" />
                    {favorite ? "已收藏，记录保存在本机浏览器" : "收藏后可在本机随时找回这部作品"}
                  </p>
                </div>
              </section>

              <section className="mt-4" aria-label="相关推荐">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-base font-bold text-foreground-950">相关推荐</h2>
                  <Link to={`/category/${movie.type}`} className="text-xs font-medium text-primary-700 hover:underline">
                    更多同类
                  </Link>
                </div>
                <div className="space-y-2.5">
                  {related.map((item) => (
                    <Link
                      key={item.id}
                      to={`/detail/${item.id}`}
                      className="group flex cursor-pointer items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-2.5 transition-colors hover:border-primary-300"
                    >
                      <div className="h-20 w-14 shrink-0 overflow-hidden rounded bg-secondary-100">
                        <img
                          src={item.poster}
                          alt={`《${item.title}》封面`}
                          title={item.title}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-1 text-sm font-semibold text-foreground-900 group-hover:text-primary-700">
                          {item.title}
                        </p>
                        <p className="mt-1 line-clamp-1 text-xs text-foreground-500">
                          {TYPE_LABELS[item.type]} · {item.year}
                        </p>
                        <p className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-accent-700">
                          <i className="ri-star-fill" />
                          {item.rating.toFixed(1)}
                          <span className="ml-1 font-normal text-foreground-400">{item.region}</span>
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>

              <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-background-200 bg-background-50 p-3.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-information-line" />
                </span>
                <p className="text-[11px] leading-relaxed text-foreground-500">
                  本站内容为虚构示例，仅用于展示影视资料。播放与下载入口均为演示，不提供真实片源。
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}